import React, { useEffect, useState } from "react"
import { Alert, Badge, Button, Card, Form, InputGroup, Modal, Spinner, Table } from "react-bootstrap"
import moment from "moment"
import API from "@/common/API"

interface EmailRecord {
  id: string
  subject: string
  to: string
  from: string
  date: string
  openCount?: number
}

const EmailLogComponent: React.FC = () => {
  const [emails, setEmails] = useState<EmailRecord[]>([])
  const [search, setSearch] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)

  // Test Email Modal
  const [showTestModal, setShowTestModal] = useState(false)
  const [targetEmail, setTargetEmail] = useState("melvin.gonzalez@shearly.com")
  const [isSendingTest, setIsSendingTest] = useState(false)
  const [testResult, setTestResult] = useState<string | null>(null)
  const [testError, setTestError] = useState<string | null>(null)

  // Preview Modal
  const [previewEmail, setPreviewEmail] = useState<EmailRecord | null>(null)

  const loadEmails = async (pageIndex = 0, searchFilter = search) => {
    try {
      setIsLoading(true)
      const query = `Page=${pageIndex}&Quantity=50&Search=${encodeURIComponent(searchFilter)}`
      const res = await API.getAction("Platform/EmailLog", query)
      if (res.data.status === "ok") {
        setEmails(res.data.response || [])
        setTotalPages(res.data.quantityOfPages || 1)
        setTotalRecords(res.data.quantityOfRecords || 0)
        setPage(pageIndex)
      }
    } catch (ex) {
      console.error("Error loading email log:", ex)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      loadEmails(0, search)
    }, 350)
    return () => clearTimeout(timer)
  }, [search])

  const handleSendTestEmail = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!targetEmail.trim()) return
    try {
      setIsSendingTest(true)
      setTestResult(null)
      setTestError(null)

      const query = `overrideEmail=${encodeURIComponent(targetEmail.trim())}`
      const res = await API.getAction("Organization/SendRevisitRemindersNow", query)
      
      if (res.data.status === "ok") {
        const resp = res.data.response || {}
        setTestResult(`Resumen de re-visitas enviado con éxito a ${targetEmail}. Registros procesados: ${resp.sentCount ?? 0}`)
        loadEmails(0, search)
      } else {
        setTestError(res.data.message || "Error al enviar correo de prueba.")
      }
    } catch (ex: any) {
      setTestError(ex.message || "Error de conexión con el servidor.")
    } finally {
      setIsSendingTest(false)
    }
  }

  return (
    <Card className="border-0 shadow-sm rounded-3">
      <Card.Header className="bg-white py-3 border-bottom d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div className="d-flex align-items-center gap-2">
          <i className="fas fa-envelope-open-text text-primary me-1 fs-5" />
          <div>
            <h6 className="fw-bold text-dark mb-0">Historial de Correos y Notificaciones</h6>
            <small className="text-muted">Registro de boletines semanales, alertas y notificaciones del sistema ({totalRecords} registros)</small>
          </div>
        </div>

        <div className="d-flex align-items-center gap-2">
          <InputGroup size="sm" style={{ width: 260 }}>
            <InputGroup.Text className="bg-white border-end-0 text-muted">
              <i className="fas fa-search" />
            </InputGroup.Text>
            <Form.Control
              className="border-start-0 ps-0 shadow-none"
              placeholder="Buscar destinatario o asunto..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </InputGroup>

          <Button
            variant="success"
            size="sm"
            className="fw-semibold px-3 text-nowrap rounded-3"
            onClick={() => {
              setTestResult(null)
              setTestError(null)
              setShowTestModal(true)
            }}
          >
            <i className="fas fa-paper-plane me-1" />
            Prueba de Envío Inmediato
          </Button>
        </div>
      </Card.Header>

      <Card.Body className="p-0">
        {isLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" size="sm" className="me-2" />
            <span className="small text-muted">Cargando registro de correos...</span>
          </div>
        ) : emails.length === 0 ? (
          <Alert variant="light" className="text-center py-5 border-0 m-0 text-muted small">
            <i className="fas fa-inbox fa-2x mb-2 text-muted" />
            <div>No se registraron envíos de correo con los criterios especificados.</div>
          </Alert>
        ) : (
          <Table hover responsive className="align-middle small mb-0">
            <thead className="table-light">
              <tr>
                <th className="ps-3">FECHA DE ENVÍO</th>
                <th>DESTINATARIO</th>
                <th>ASUNTO</th>
                <th>REMITENTE</th>
                <th className="text-center">APERTURAS</th>
                <th className="text-end pe-3">ACCIONES</th>
              </tr>
            </thead>
            <tbody>
              {emails.map(m => (
                <tr key={m.id}>
                  <td className="ps-3 fw-semibold text-dark">
                    <i className="far fa-clock me-1 text-primary" />
                    {m.date ? moment(m.date).format("DD/MM/YYYY HH:mm") : "—"}
                  </td>
                  <td>
                    <Badge bg="light" text="dark" className="border font-monospace">
                      {m.to}
                    </Badge>
                  </td>
                  <td className="fw-bold text-dark">{m.subject}</td>
                  <td className="text-muted">{m.from || "Sistema Shearly"}</td>
                  <td className="text-center">
                    <Badge bg={m.openCount && m.openCount > 0 ? "success" : "secondary"} pill>
                      {m.openCount || 0} lecturas
                    </Badge>
                  </td>
                  <td className="text-end pe-3">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="py-0 px-2"
                      title="Ver Detalles"
                      onClick={() => setPreviewEmail(m)}
                    >
                      <i className="fas fa-eye me-1" />
                      Detalles
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>

      {/* Test Email Modal */}
      <Modal show={showTestModal} onHide={() => setShowTestModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="h6 fw-bold">
            <i className="fas fa-paper-plane text-success me-2" />
            Disparar Prueba de Boletín de Re-visitas
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSendTestEmail}>
          <Modal.Body className="small">
            <p className="text-muted mb-3">
              Genera y envía en tiempo real el boletín de resumen con todas las re-visitas asignadas pendientes en el sistema hacia la dirección de correo indicada.
            </p>

            {testResult && <Alert variant="success" dismissible onClose={() => setTestResult(null)}>{testResult}</Alert>}
            {testError && <Alert variant="danger" dismissible onClose={() => setTestError(null)}>{testError}</Alert>}

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Correo de Destino *</Form.Label>
              <Form.Control
                type="email"
                size="sm"
                placeholder="ejemplo@shearly.com"
                value={targetEmail}
                onChange={e => setTargetEmail(e.target.value)}
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="py-2">
            <Button variant="secondary" size="sm" onClick={() => setShowTestModal(false)}>
              Cerrar
            </Button>
            <Button variant="success" size="sm" type="submit" disabled={isSendingTest}>
              {isSendingTest ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Enviando...
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane me-1" />
                  Enviar Boletín de Prueba
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Preview Detail Modal */}
      <Modal show={!!previewEmail} onHide={() => setPreviewEmail(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="h6 fw-bold">
            Detalles del Correo Enviado
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="small">
          {previewEmail && (
            <div>
              <div className="mb-2"><strong>Asunto:</strong> {previewEmail.subject}</div>
              <div className="mb-2"><strong>Para:</strong> {previewEmail.to}</div>
              <div className="mb-2"><strong>De:</strong> {previewEmail.from}</div>
              <div className="mb-3"><strong>Fecha:</strong> {moment(previewEmail.date).format("DD/MM/YYYY HH:mm")}</div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="py-2">
          <Button variant="secondary" size="sm" onClick={() => setPreviewEmail(null)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  )
}

export default EmailLogComponent
